import React from 'react';
import { Flex, Typography, theme } from 'antd';
import dayjs from 'dayjs';

import { FiltersForPrint } from '~components/Filters';
import { PrintableExport } from '~components/PrintableExport';
import { Page, PageContent } from '~components/PrintableLayout';
import { IsPrintingProvider } from '~library/print';
import { purifyHtml } from '~library/purifyHtml';
import CrawlerActivityChart from './CrawlerActivityChart';
import DiscoverabilityKPIs, {
  aiCrawlersDetectedTooltip,
  ERROR_RATE_TOOLTIP,
  PAGES_CRAWLED_TOOLTIP,
} from './DiscoverabilityKPIs';
import {
  CRAWL_REQUESTS_TAB_TOOLTIP,
  ERROR_RATE_TAB_TOOLTIP,
} from './CrawlerActivityChartsSection';
import DiscoverabilityStatusBanner, {
  bannerExplanations,
} from './DiscoverabilityStatusBanner';
import { ROLLING_AVERAGE_TOOLTIP } from './CrawlerActivityChartLegend';
import {
  useGetErrorRateChartQuery,
  useGetPagesCrawledChartQuery,
  type DiscoverabilityChartQueryParams,
} from '../api/discoverabilityChartsApi';
import { useGetDiscoverabilityKpisQuery } from '../api/discoverabilityKpisApi';
import { useGetDiscoverabilityStatusQuery } from '../api/discoverabilityStatusApi';
import { CRAWLER_ID_TO_CATEGORY } from '../DiscoverabilityCrawlers';
import { useAiCrawlerLineup } from '../../shared';
import { useDiscoverabilitySearchParams } from '../hooks/useDiscoverabilitySearchParams';
import { USER_TIMEZONE } from '../userTimezone';

// Print-only downscale, defined in pages/print.css.
const EXPORT_SCALE_CLASS = 'discoverability-export-scale';

// Mirrors the on-screen section subtitle in CrawlerActivityChartsSection.
const CRAWLER_ACTIVITY_SUBTITLE =
  'Track the rolling average of AI Crawler activity and error rates over time.';

const NO_OP = () => undefined;
const NO_HIDDEN_SERIES: ReadonlySet<string> = new Set();

// Folds the URL's granular crawler ids into the provider labels the reader
// recognises ("ChatGPT", not "gptbot, chatgpt"), deduped — several ids map to
// one provider. FiltersForPrint's own id→label mapping is dashboard-v3's and
// doesn't know these ids, hence the pre-resolved labels.
function crawlerLabelsFor(crawlers: string[] | undefined): string[] | undefined {
  if (!crawlers || crawlers.length === 0) return undefined;
  const categories = Array.from(
    new Set(
      crawlers
        .map((id) => CRAWLER_ID_TO_CATEGORY[id])
        .filter((category): category is string => Boolean(category))
    )
  );
  return categories.length > 0 ? categories : undefined;
}

/**
 * A tooltip's copy rendered as body text. On screen these explanations sit
 * behind info icons; on paper the icons are dead ink, so the export prints the
 * copy — matching PrintableDashboard and PrintableCDNAnalytics, which do the
 * same for their KPIs and charts. Some of these strings are HTML (the error
 * rate is a bulleted list), hence the sanitized innerHTML rather than a child.
 */
// Tooltip copy uses <br /> pairs to separate a follow-up thought inside a hover
// card. Printed as a bullet, those breaks drop the follow-up onto its own line
// below the marker, where it reads as a stray sentence rather than part of the
// point — so the copy is flowed into one bullet instead.
function flowLineBreaks(message: string): string {
  return message.replace(/(?:<br\s*\/?>\s*)+/gi, ' ');
}

function TooltipParagraph({ label, message }: { label: string; message: string }) {
  return (
    <Typography.Paragraph>
      <Typography.Text strong>{label}: </Typography.Text>
      <span dangerouslySetInnerHTML={{ __html: purifyHtml(message) }} />
    </Typography.Paragraph>
  );
}

/**
 * The Discoverability tab as it appears in the exported PDF: filter summary,
 * status banner (read-only), KPIs and both AI Crawler Activity charts.
 *
 * It re-reads the filters from the export tab's own URL, so the PDF reflects
 * the state that was on screen at click time. Both chart series are fetched
 * unconditionally — the on-screen section skips whichever tab isn't active,
 * but a PDF has no tabs and shows both.
 */
function PrintableDiscoverability() {
  const { token } = theme.useToken();

  const { dateFrom, dateTo, crawlers, domain } = useDiscoverabilitySearchParams();
  const lineup = useAiCrawlerLineup();
  const chartParams = React.useMemo<DiscoverabilityChartQueryParams>(
    () => ({ dateFrom, dateTo, crawlers, domain, timezone: USER_TIMEZONE }),
    [dateFrom, dateTo, crawlers, domain]
  );

  const statusQuery = useGetDiscoverabilityStatusQuery();
  const kpisQuery = useGetDiscoverabilityKpisQuery(chartParams);
  const pagesCrawledQuery = useGetPagesCrawledChartQuery(chartParams);
  const errorRateQuery = useGetErrorRateChartQuery(chartParams);

  const isEverythingLoaded =
    !statusQuery.isFetching &&
    !kpisQuery.isFetching &&
    !pagesCrawledQuery.isFetching &&
    !errorRateQuery.isFetching;

  return (
    <IsPrintingProvider initialValue={true}>
      <PrintableExport subject="Discoverability" ready={isEverythingLoaded}>
        {/* One <Page> per printed sheet: `.printable-page` is a fixed 282mm box
            (256mm in Safari) with `overflow: hidden`, so each sheet's contents
            must be laid out to fit rather than relying on them to flow — anything
            past the box is silently dropped from the PDF.

            The split follows the reader rather than the source order: findings and
            the first chart lead on sheet one, and the reference copy explaining
            the KPI cards trails the charts on sheet two. Ordering it the other way
            round left both sheets about half empty, with a page break standing
            where the first chart should have been. */}
        <Page>
          <PageContent>
            <div className={EXPORT_SCALE_CLASS}>
              <FiltersForPrint
                domainSelected={domain}
                crawlerLabels={crawlerLabelsFor(crawlers)}
                dateFrom={dayjs(dateFrom).toDate()}
                dateTo={dayjs(dateTo).toDate()}
              />

              <Flex vertical gap={token.marginLG}>
                {statusQuery.data && (
                  <div>
                    <DiscoverabilityStatusBanner
                      status={statusQuery.data}
                      onDismiss={NO_OP}
                      onGoToDiagnostics={NO_OP}
                      onInspectPages={NO_OP}
                      readOnly
                    />

                    {/* Listed plainly rather than labelled: unlike the KPI and
                        chart explanations, these tooltips hang off dynamic banner
                        text with no heading to reuse, and the banner immediately
                        above is the context they need. Only the findings the
                        banner actually showed are explained. */}
                    <Typography.Paragraph style={{ marginBlockStart: token.margin }}>
                      <ul>
                        {bannerExplanations(lineup, statusQuery.data).map((message) => (
                          <li
                            key={message}
                            dangerouslySetInnerHTML={{
                              __html: purifyHtml(flowLineBreaks(message)),
                            }}
                          />
                        ))}
                      </ul>
                    </Typography.Paragraph>
                  </div>
                )}

                <DiscoverabilityKPIs />

                <Flex vertical gap={token.marginXS}>
                  <Typography.Title level={4} style={{ margin: 0, fontSize: token.fontSizeHeading4 }}>
                    AI Crawler Activity
                  </Typography.Title>
                  <Typography.Text type="secondary" style={{ fontSize: token.fontSize }}>
                    {CRAWLER_ACTIVITY_SUBTITLE}
                  </Typography.Text>

                  {/* The legend this describes is shared by both charts, so it is
                      stated once beside the first rather than repeated above each. */}
                  <TooltipParagraph label="7-day rolling average" message={ROLLING_AVERAGE_TOOLTIP} />

                  <TooltipParagraph label="Crawl Requests" message={CRAWL_REQUESTS_TAB_TOOLTIP} />
                  <CrawlerActivityChart
                    valueFormat="integer"
                    data={pagesCrawledQuery.data}
                    isLoading={pagesCrawledQuery.isLoading}
                    hiddenSeries={NO_HIDDEN_SERIES}
                    onSeriesToggled={NO_OP}
                  />
                </Flex>
              </Flex>
            </div>
          </PageContent>
        </Page>

        <Page>
          <PageContent>
            <div className={EXPORT_SCALE_CLASS}>
              <Flex vertical gap={token.marginLG}>
                <Flex vertical gap={token.marginXS}>
                  <TooltipParagraph label="Error Rate" message={ERROR_RATE_TAB_TOOLTIP} />
                  <CrawlerActivityChart
                    valueFormat="percent"
                    data={errorRateQuery.data}
                    isLoading={errorRateQuery.isLoading}
                    hiddenSeries={NO_HIDDEN_SERIES}
                    onSeriesToggled={NO_OP}
                  />
                </Flex>

                <div>
                  <TooltipParagraph
                    label="AI Crawlers detected"
                    message={aiCrawlersDetectedTooltip(lineup)}
                  />
                  <TooltipParagraph label="Pages Crawled (unique)" message={PAGES_CRAWLED_TOOLTIP} />
                  <TooltipParagraph label="Error rate" message={ERROR_RATE_TOOLTIP} />
                </div>
              </Flex>
            </div>
          </PageContent>
        </Page>
      </PrintableExport>
    </IsPrintingProvider>
  );
}

export default PrintableDiscoverability;
